import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import { toggleCloudFlareModal, toggleSpinner, updateAuthPromoBlock, updateMessages } from 'apps/promo/actions/appAction';
import { addExtras } from 'apps/promo/actions/extraAction';
import { changeFormField, scrollToField, setFieldError, setNewForm } from 'apps/promo/actions/formAction';
import { updatePageMessages } from 'apps/promo/actions/messageAction';
import { FixedBottomPanel } from 'apps/promo/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import PopupNew from 'apps/promo/components/common/Popup/PopupNew';
import { AUTOCOMPLETE_POST_CODE, FILE } from 'apps/promo/constants/FieldType';
import { fieldPropType } from 'apps/promo/helpers/customPropTypes';
import { useForms } from 'apps/promo/hooks/selectors';
import { FormService } from 'apps/promo/services/FormService';

const Form = ({ getCanonicalPath = false, validateForm = true, checkNotVisibleField = false, submitByEnter = true, fieldsForSubmit = null, ...props }) => {
    const forms = useForms();
    const { fields, action: reduxAction, validateForm: reduxValidateForm } = forms[props.formName] ?? {};

    const popupRef = useRef(null);
    const controller = useRef();
    const { current: formService } = useRef(new FormService(getCanonicalPath, controller, props.formName));

    useEffect(() => {
        controller.current = __isBrowser__ ? new AbortController() : {};
        formService.setUpdateRedux(props.updateMessages, props.updateAuthPromoBlock, props.updatePageMessages, props.addExtras);
        formService.setAppAction(
            props.setFieldError,
            props.setNewForm,
            props.changeFormField,
            props.scrollToField,
            props.toggleSpinner,
            props.toggleCloudFlareModal,
        );
        formService.setPopups(popupRef);
    }, []);

    useEffect(() => {
        formService.setHandlers(props.beforeRedirect, props.beforeSubmit, props.onErrorHandler, props.onSubmitHandler);
    }, [props.beforeRedirect, props.beforeSubmit, props.onErrorHandler, props.onSubmitHandler]);

    useEffect(() => {
        formService.setAction(reduxAction, props.action);
    }, [reduxAction, props.action]);

    useEffect(() => {
        formService.setFormProps(reduxValidateForm, validateForm, checkNotVisibleField);
    }, [reduxValidateForm, validateForm, checkNotVisibleField]);

    const handleKeyDown = (e) => {
        if (!submitByEnter && e.keyCode === 13) {
            e.preventDefault();
        }
    };

    return (
        <>
            <form
                id={props.id}
                data-testid={props.dataTestId}
                method="POST"
                action={formService.getAction()}
                className={props.className}
                onKeyDown={handleKeyDown}
                onSubmit={(e) => {
                    let newFieldsForSubmit = fields;
                    if (Array.isArray(props.additionalFormNames) && props.additionalFormNames?.length > 0 && forms && Object.values(forms)?.length > 1) {
                        props.additionalFormNames.forEach((formName) => {
                            newFieldsForSubmit = { ...forms[formName]?.fields, ...newFieldsForSubmit };
                        });
                    }
                    if (Array.isArray(fieldsForSubmit) && fieldsForSubmit.length > 0) {
                        newFieldsForSubmit = {};
                        fieldsForSubmit.forEach((field) => {
                            newFieldsForSubmit[field.name] = fields[field.name];
                        });
                    }
                    formService.handlerSubmit(e, newFieldsForSubmit);
                }}
            >
                {props.children}
                {props.buttons && <FixedBottomPanel>{props.buttons}</FixedBottomPanel>}
            </form>
            <PopupNew ref={popupRef} />
        </>
    );
};

Form.propTypes = {
    formName: PropTypes.string.isRequired,
    additionalFormNames: PropTypes.arrayOf(PropTypes.string),
    action: PropTypes.string,
    getCanonicalPath: PropTypes.bool,
    onSubmitHandler: PropTypes.func,
    className: PropTypes.string,
    buttons: PropTypes.object,
    fieldsForSubmit: PropTypes.array,
    fields: PropTypes.objectOf(
        fieldPropType([
            'textarea',
            'drop_box',
            'sbp_drop_box',
            'tel',
            'email',
            'number',
            'password',
            'search',
            'text',
            'short_text',
            'attachment',
            'url',
            'date',
            'hidden',
            'pan',
            'cvv',
            'exp_date',
            'checkbox',
            'bik',
            'bank_account',
            'phone',
            'phone_strict',
            'autocomplete_address',
            AUTOCOMPLETE_POST_CODE,
            'phone_country',
            'upload_doc',
            'digital',
            'country',
            FILE,
        ]),
    ),
};

export default connect(null, {
    changeFormField,
    scrollToField,
    toggleSpinner,
    updateMessages,
    updateAuthPromoBlock,
    updatePageMessages,
    setFieldError,
    setNewForm,
    addExtras,
    toggleCloudFlareModal,
})(Form);
